import { useCallback, useRef, useState } from 'react'

import { NetworkStatus } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

const STREAK_HISTORY_PAGE_SIZE = 20

export const WeeklyPostingStreakHistoryQuery = graphql(/* GraphQL */ `
  query WeeklyPostingStreakHistory(
    $input: WeeklyPostingStreakHistoryInput!
    $first: Int!
    $after: String
  ) {
    weeklyPostingStreakHistory(input: $input, first: $first, after: $after) {
      summary {
        currentStreak {
          startedAt
          endedAt
          weekCount
        }
        longestStreak {
          startedAt
          endedAt
          weekCount
        }
        firstStreak {
          startedAt
          endedAt
          weekCount
        }
      }
      edges {
        cursor
        node {
          startedAt
          endedAt
          weekCount
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

export type WeeklyPostingStreakHistoryData = ResultOf<
  typeof WeeklyPostingStreakHistoryQuery
>

type WeeklyPostingStreakHistory =
  WeeklyPostingStreakHistoryData['weeklyPostingStreakHistory']

type UseWeeklyPostingStreakHistoryResult = {
  summary: WeeklyPostingStreakHistory['summary'] | undefined
  periods: WeeklyPostingStreakHistory['edges']
  loading: boolean
  error: ErrorLike | undefined
  accessDenied: boolean
  retry: () => void
  hasNextPage: boolean
  loadingMore: boolean
  loadMore: () => Promise<void>
  loadMoreError: boolean
}

function isForbiddenError(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      (graphQLError) => graphQLError.extensions?.code === 'FORBIDDEN',
    )
  )
}

export function useWeeklyPostingStreakHistory({
  organizationId,
  skip,
}: {
  organizationId: string | null
  skip: boolean
}): UseWeeklyPostingStreakHistoryResult {
  const [loadMoreError, setLoadMoreError] = useState(false)
  const currentOrganizationId = useRef(organizationId)
  currentOrganizationId.current = organizationId
  const query = useQuery(WeeklyPostingStreakHistoryQuery, {
    variables: {
      input: { organizationId: organizationId ?? '' },
      first: STREAK_HISTORY_PAGE_SIZE,
      after: null,
    },
    skip: skip || !organizationId,
    notifyOnNetworkStatusChange: true,
  })

  const history = query.data?.weeklyPostingStreakHistory
  const endCursor = history?.pageInfo.endCursor
  const hasNextPage = history?.pageInfo.hasNextPage ?? false
  const loadingMore = query.networkStatus === NetworkStatus.fetchMore
  const { fetchMore, refetch } = query

  const loadMore = useCallback(async (): Promise<void> => {
    if (!organizationId || !endCursor || !hasNextPage || loadingMore) return

    setLoadMoreError(false)

    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          const previousHistory = previous.weeklyPostingStreakHistory
          const nextHistory = fetchMoreResult?.weeklyPostingStreakHistory
          if (
            !nextHistory ||
            currentOrganizationId.current !== organizationId ||
            previousHistory.pageInfo.endCursor !== endCursor
          ) {
            return previous
          }

          const existingCursors = new Set(
            previousHistory.edges.map((edge) => edge.cursor),
          )
          const newEdges = nextHistory.edges.filter(
            (edge) => !existingCursors.has(edge.cursor),
          )

          return {
            weeklyPostingStreakHistory: {
              ...nextHistory,
              summary: previousHistory.summary,
              edges: [...previousHistory.edges, ...newEdges],
            },
          }
        },
      })
    } catch {
      setLoadMoreError(true)
    }
  }, [endCursor, fetchMore, hasNextPage, loadingMore, organizationId])

  const retry = useCallback((): void => {
    void refetch().catch(() => undefined)
  }, [refetch])

  return {
    summary: history?.summary,
    periods: history?.edges ?? [],
    loading: query.loading && !query.data,
    error: query.data ? undefined : query.error,
    accessDenied: isForbiddenError(query.error),
    retry,
    hasNextPage,
    loadingMore,
    loadMore,
    loadMoreError,
  }
}
